import { useProfile } from '@/auth/ProfileContext'
import { useSearchParams } from 'react-router-dom'
import { TaskPipeline } from '@/components/TaskPipeline'
import { pipelineFilterKey, intakeStage, workflowStage, taskDisplayStage } from '@/lib/stages'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { useAsync } from '@/components/useAsync'
import { Section } from '@/components/Section'
import { TaskRows } from '@/components/TaskRows'
import { needsBuilderReply, needsScienceAnswer } from '@/lib/stages'
import type { Task } from '@/lib/types'
import { isScienceExpert } from '@/lib/programs'
import { IntakeRows } from '@/pages/scientific/IntakeRows'
import { isSubmitted, type IntakeSummary } from '@/lib/templateIntakes'
import { ScienceCreateButton } from '@/components/ScienceCreateButton'

interface Dashboard { template_intakes_enabled?: boolean; template_intakes?: IntakeSummary[]; template_intake_queue?: IntakeSummary[]; tasks: Task[]; counts: { action_needed: number; in_review: number; done: number } }

export default function SmeDashboard() {
  const { profile } = useProfile()
  const [params, setParams] = useSearchParams()
  const { data, error, loading } = useAsync(() => api.get<Dashboard>('/sme/dashboard'))
  if (loading) return <p className="muted">Loading…</p>
  if (error || !data) return <div className="note err">{error}</div>

  return <DashboardView tasks={data.tasks} stage={params.get('stage') ?? ''} onStage={value => setParams(value ? { stage: value } : {})}
    templateIntakes={isScienceExpert(profile) && data.template_intakes_enabled ? data.template_intakes ?? [] : undefined}
    intakeQueue={isScienceExpert(profile) && data.template_intakes_enabled ? data.template_intake_queue ?? [] : undefined}
    newTaskHref={isScienceExpert(profile) ? '/sme/templates/new' : '/sme/submit'}
    newTaskLabel={isScienceExpert(profile) ? 'New template' : 'New task'}
    introduction={!profile?.tutorial && profile?.onboarding_version !== 1} />
}

/** The task list presentation is shared with the browser-only introduction. */
export function DashboardView({ tasks, stage, onStage, introduction = false, taskHref, newTaskHref = '/sme/submit', newTaskLabel = 'New task', templateIntakes, intakeQueue }: {
  tasks: Task[]; stage: string; onStage: (stage: string) => void; introduction?: boolean
  templateIntakes?: IntakeSummary[]; intakeQueue?: IntakeSummary[]
  taskHref?: (task: Task) => string; newTaskHref?: string; newTaskLabel?: string
}) {
  const retired = tasks.filter(t => t.lifecycle === 'retired')
  const noun = newTaskLabel === 'New template' ? 'workflow' : 'task'
  const scientific = (noun === 'workflow' || tasks.length > 0) && tasks.every(t => t.kind === 'scientific_workflow' || t.collection)
  if (scientific) stage = pipelineFilterKey(stage)
  const visible = tasks.filter(t => t.lifecycle !== 'retired' && (!stage || (scientific ? workflowStage(t) : String(taskDisplayStage(t))) === stage))
  const reply = (t: Task) => needsBuilderReply(t) || needsScienceAnswer(t)
  const needsMe = visible.filter((t) => reply(t) || (t.expert_action_needed ?? (t.stage === 0 || t.stage === 2)))
    .sort((a, b) => Number(reply(b)) - Number(reply(a)))
  const waiting = visible.filter((t) => !needsMe.includes(t) && t.stage !== 5)
  const done = visible.filter((t) => t.stage === 5)
  // Selecting a stage chip filters to exactly the templates and workflows that stage counts. The
  // claimable queue is another expert's template, never one of this expert's own, so the two
  // lists are disjoint and the chips count both.
  const inStage = (template: IntakeSummary) => !stage || intakeStage(template.status) === stage
  const counted = templateIntakes && [...templateIntakes, ...(intakeQueue ?? [])]
  const intakes = (templateIntakes ?? []).filter(inStage)
  const intakeDrafts = intakes.filter(t => t.status === 'draft' || t.status === 'returned')
  // An approved template stays here: it is how an expert follows the workflows it produced.
  const intakeWaiting = intakes.filter(t => isSubmitted(t.status) || t.status === 'approved')
  // Proposals any Physics expert may claim, the way the peer review queue is pulled.
  const queue = (intakeQueue ?? []).filter(inStage)

  return (
    <div>
      <div className="page-head">
        <div><h1>{templateIntakes ? 'My data' : `My ${noun}s`}</h1></div>
        {templateIntakes ? <ScienceCreateButton /> : <Link className="btn primary" to={newTaskHref}>+ {newTaskLabel}</Link>}
      </div>

      {introduction && <p className="note"><button className="link" onClick={() => window.dispatchEvent(new Event('expert-introduction'))}>Read the platform introduction</button></p>}
      <TaskPipeline scientific={scientific} tasks={tasks} intakes={counted} selected={stage} onSelect={onStage} />
      {stage && <button className="link" onClick={() => onStage('')}>Show all {noun}s</button>}
      {tasks.length === 0 && !templateIntakes?.length && (
        <div className="box subtle">
          <h2>{templateIntakes ? 'No data yet' : `No ${noun}s yet`}</h2>
          <p>Read the <Link to="/sme/guide">guide</Link>, then <Link to={newTaskHref}>{noun === 'workflow' ? 'submit your first template' : 'create your first task'}</Link>.</p>
        </div>
      )}

      <Section title="Waiting for you" count={needsMe.length + intakeDrafts.length + queue.length}>
        {(needsMe.length > 0 || !(intakeDrafts.length + queue.length)) && <TaskRows tasks={needsMe} role="sme" taskHref={taskHref} showTodo empty="Nothing needs your attention right now." />}
        <IntakeRows templates={[...queue, ...intakeDrafts]} />
      </Section>

      <Section title="In review or build" count={waiting.length + intakeWaiting.length}>
        {(waiting.length > 0 || !intakeWaiting.length) && <TaskRows tasks={waiting} role="sme" taskHref={taskHref} showTodo empty={`No ${noun}s in review or build.`} />}
        <IntakeRows templates={intakeWaiting} />
      </Section>
      <Section title="Completed" count={done.length}><TaskRows tasks={done} role="sme" taskHref={taskHref} empty={`No completed ${noun}s yet.`} /></Section>
      {retired.length > 0 && <Section title="Retired" count={retired.length}><TaskRows tasks={retired} role="sme" taskHref={taskHref} empty={`No retired ${noun}s.`} /></Section>}
    </div>
  )
}
